import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MarkSold } from '@miraki/contracts';
import { api, ApiError } from '../../api/client';
import { useDeliveryReconciliation, useShopConsignmentInvoice } from '../../api/hooks';
import type { PieceBucket, ReconciledPiece, ShopConsignmentInvoice } from '../../api/types';
import { useSession } from '../../auth/session';
import { Alert, Button, Chip, Empty, Field, Modal, Money, Note, PageHeader, Panel, Spinner, Table } from '../../ui';
import { PAYMENT_TONE, SHIPMENT_TONE } from './consignment-invoice-tone';
import { isZeroAmount, payableStatusKey } from '../despatch-payable';
import { backFromInvoice, INVOICES_HOME, ORDERS_HOME, stateAfterBack } from './back-target';
import { shipmentStateLabel } from '../shipment-state';
import { notYet, saleDateBounds, type NotYet } from './consignment-read';
import { actionsFor, payablesLinkFor } from './delivery-tally';
import { invalidateAll } from '../../api/write-keys';

const dash = '—';

/** Copy to the clipboard; falls back to a hidden textarea where the async API is unavailable (plain http, older browsers). */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function TrackingNumber({ value }: { value: string | null }) {
  const { t } = useTranslation();
  const [copy, setCopy] = useState<'idle' | 'done' | 'failed'>('idle');

  if (!value) return <span className="mk-dim">{t('shop.consignmentInvoice.noTracking')}</span>;
  return (
    <span className="mk-pi-track">
      <strong className="mk-pi-track-no" data-testid="tracking-number">{value}</strong>
      <Button
        size="sm"
        tone="ghost"
        onClick={() => {
          void copyText(value).then((ok) => {
            setCopy(ok ? 'done' : 'failed');
            window.setTimeout(() => setCopy('idle'), 2000);
          });
        }}
      >
        {copy === 'done' ? t('shop.consignmentInvoice.copied')
          : copy === 'failed' ? t('shop.consignmentInvoice.copyFailed')
            : t('shop.consignmentInvoice.copy')}
      </Button>
    </span>
  );
}

function Line({ label, children, strong }: { label: string; children: React.ReactNode; strong?: boolean }) {
  return (
    <div className={strong ? 'mk-pi-line mk-pi-strong' : 'mk-pi-line'}>
      <span className={strong ? undefined : 'mk-dim'}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

function ShipmentPanel({ pi }: { pi: ShopConsignmentInvoice }) {
  const { t } = useTranslation();
  const s = pi.shipment;
  return (
    <Panel title={t('shop.consignmentInvoice.shipmentTitle')} pad>
      {!s ? (
        <p className="mk-dim" data-testid="no-shipment">{t('shop.consignmentInvoice.noShipment')}</p>
      ) : (
        <>
          <div className="mk-pi-ship">
            <div>
              <div className="mk-dim mk-pi-cap">{t('shop.consignmentInvoice.trackingNumber')}</div>
              <TrackingNumber value={s.trackingNumber} />
            </div>
            <div>
              <div className="mk-dim mk-pi-cap">{t('shop.consignmentInvoice.shipmentStatus')}</div>
              <Chip tone={SHIPMENT_TONE[s.status]}>{shipmentStateLabel(s.status)}</Chip>
            </div>
          </div>
          <Line label={t('shop.consignmentInvoice.shipmentReference')}>{s.reference}</Line>
          <Line label={t('shop.consignmentInvoice.courierName')}>{s.courierName ?? dash}</Line>
          {s.dispatchedAt ? (
            <Line label={t('shop.consignmentInvoice.dispatchedLabel')}>
              {new Date(s.dispatchedAt).toLocaleString()}
            </Line>
          ) : null}
          {s.deliveredAt ? (
            <Line label={t('shop.consignmentInvoice.deliveredLabel')}>
              {new Date(s.deliveredAt).toLocaleString()}
            </Line>
          ) : null}
          {s.status === 'PACKED' ? <Alert tone="done">{t('shop.consignmentInvoice.readyToSend')}</Alert> : null}
          {s.status === 'AWAITING_PAYMENT' ? (
            <Alert tone="run">{t('shop.consignmentInvoice.awaitingPayment')}</Alert>
          ) : null}
        </>
      )}
    </Panel>
  );
}

const BUCKET_TONE: Record<PieceBucket, 'us' | 'done' | 'run' | 'stop'> = {
  incoming: 'run', held: 'us', sold: 'done', returned: 'us', other: 'stop',
};

/**
 * SH-10…SH-12 — the tally.
 *
 * One row per PIECE, because the piece is what gets sold, returned, invoiced and paid. The
 * delivery is only the batch they arrived in: it is never the unit of settlement, which is the
 * misreading the Amounts panel below still encourages.
 *
 * Money columns are rendered only when the server SENT them. A Buyer receives no money keys at
 * all (DB-07), so this is not a screen hiding what it was given — there is nothing to hide.
 *
 * **W4 and W5 — the two actions.** Both are rendered conditionally and neither is ever disabled:
 * DB-07's rule is that a control the server would refuse is not offered, so a user without
 * `shop.report_sale` sees no sale button and a Buyer no invoice link, rather than a greyed-out
 * promise. `actionsFor` decides, so the rule is testable without a browser.
 *
 * "View invoice" is a link to R-22 and is **never** labelled Pay: under FN-06 the retailer pays by
 * bank transfer and Admin confirms it when it arrives. There is nothing to pay here, and a button
 * saying otherwise is the class of defect this whole screen was built to remove.
 *
 * ⚠ **EVERY "invoice" in this panel is the PURCHASE invoice, not the consignment invoice the screen is
 * about.** `shop.reconciliation.colInvoice` / `.notInvoiced` / `.invoiced` / `.viewInvoice` and
 * `piece.invoiceReference` all name the document raised AFTER a piece is reported sold — the one
 * that asks for money. D97 release 1 renames the despatch document only, so the namespace
 * (`shop.reconciliation.*`, which hangs off the consignment invoice) and its values name two
 * different documents on purpose. Do not "make them consistent".
 */
function ReconciliationPanel({ pi }: { pi: ShopConsignmentInvoice }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const qc = useQueryClient();
  const { data, isLoading, error } = useDeliveryReconciliation(pi.id);
  // A retailer's own grants. `can()` is not usable here: it short-circuits on
  // `me.kind !== 'internal'` and answers false for every retailer user.
  const permissions = me?.kind === 'retailer' ? me.permissions : [];
  const mayReportSale = permissions.includes('shop.report_sale');
  // The portal's calendar day, for SL-04's picker — from R-01's route, the one place that resolves
  // it (D49); a browser's own date is not the portal's. Read only where a sale can be reported,
  // and when it is missing the picker is omitted and the server applies its own today.
  const dashboard = useQuery({
    queryKey: ['shop', 'dashboard'],
    queryFn: () => api.get<{ today: string }>('/shop/dashboard'),
    enabled: mayReportSale,
  });
  const [confirming, setConfirming] = useState<(ReconciledPiece & { pieceId: string }) | null>(null);

  if (isLoading) return <Panel title={t('shop.reconciliation.title')} pad><Spinner /></Panel>;
  if (error || !data) return null;

  // A delivery raised but not yet shipped has no pieces. Say so: an empty table reads as a fault.
  if (data.pieces.length === 0) {
    return (
      <Panel title={t('shop.reconciliation.title')} pad>
        <Empty title={t('shop.reconciliation.notDespatched')} body={t('shop.reconciliation.notDespatchedBody')} />
      </Panel>
    );
  }

  const { totals } = data;
  // `undefined` means the server withheld it by role; it never means zero.
  const showsMoney = totals.outstanding !== undefined;
  const date = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString() : null);

  return (
    <Panel title={t('shop.reconciliation.title')}>
      <Note>{t('shop.reconciliation.note')}</Note>
      <Table
        rows={data.pieces}
        rowKey={(p) => p.pieceId ?? p.jobNumber}
        columns={[
          ...(data.pieces.some((p) => p.description !== undefined)
            ? [{ header: t('shop.reconciliation.colItem'), render: (p: ReconciledPiece) => p.description ?? dash }]
            : []),
          {
            header: t('shop.reconciliation.colValue'), numeric: true,
            render: (p: ReconciledPiece) => <Money amount={p.wholesaleValue} currency={pi.currency} />,
          },
          {
            header: t('shop.reconciliation.colStatus'),
            render: (p: ReconciledPiece) => (
              <>
                <Chip tone={BUCKET_TONE[p.bucket]}>{t(`shop.reconciliation.bucket${p.bucket.charAt(0).toUpperCase()}${p.bucket.slice(1)}`)}</Chip>
                {date(p.soldOn) ? <div className="mk-dim">{t('shop.reconciliation.soldOn', { date: date(p.soldOn) })}</div> : null}
                {date(p.returnedAt) ? <div className="mk-dim">{t('shop.reconciliation.returnedOn', { date: date(p.returnedAt) })}</div> : null}
              </>
            ),
          },
          ...(showsMoney ? [
            {
              /* The PURCHASE invoice raised over this piece after it was reported sold — NOT the
                 consignment invoice this screen is about. See the panel's doc comment. */
              header: t('shop.reconciliation.colInvoice'),
              render: (p: ReconciledPiece) => (p.invoiceReference
                ? <code>{p.invoiceReference}</code>
                : <span className="mk-dim">{t('shop.reconciliation.notInvoiced')}</span>),
            },
            {
              header: t('shop.reconciliation.colOutstanding'), numeric: true,
              // null is "nothing was ever billed for this piece" — not "zero outstanding".
              render: (p: ReconciledPiece) => (p.outstanding === null || p.outstanding === undefined
                ? <span className="mk-dim">{t('shop.reconciliation.nothingDue')}</span>
                : <Money amount={p.outstanding} currency={pi.currency} />),
            },
          ] : []),
          // W4 and W5. The column itself is absent where no row in the tally offers anything, so
          // a read-only audience gets no empty gutter.
          ...(data.pieces.some((p) => actionsFor(p, permissions).length > 0) ? [{
            header: '', width: '1%',
            render: (p: ReconciledPiece) => {
              const actions = actionsFor(p, permissions);
              const { pieceId } = p;
              return (
                <>
                  {actions.includes('REPORT_SALE') && pieceId !== undefined
                    ? (
                      <Button size="sm" onClick={() => setConfirming({ ...p, pieceId })}>
                        {t('myInventory.markSold')}
                      </Button>
                    )
                    : null}
                  {actions.includes('VIEW_INVOICE') && p.invoiceReference
                    ? <Link to={payablesLinkFor(p.invoiceReference)}>{t('shop.reconciliation.viewInvoice')}</Link>
                    : null}
                </>
              );
            },
          }] : []),
        ]}
      />
      <div className="mk-pi-totals">
        <Line label={t('shop.reconciliation.consigned')}><Money amount={totals.consigned} currency={pi.currency} /></Line>
        {totals.incoming !== '0.0000' ? <Line label={t('shop.reconciliation.incoming')}><Money amount={totals.incoming} currency={pi.currency} /></Line> : null}
        <Line label={t('shop.reconciliation.held')}><Money amount={totals.held} currency={pi.currency} /></Line>
        <Line label={t('shop.reconciliation.sold')}><Money amount={totals.sold} currency={pi.currency} /></Line>
        {totals.returned !== '0.0000' ? <Line label={t('shop.reconciliation.returned')}><Money amount={totals.returned} currency={pi.currency} /></Line> : null}
        {totals.other !== '0.0000' ? <Line label={t('shop.reconciliation.other')}><Money amount={totals.other} currency={pi.currency} /></Line> : null}
        {showsMoney ? (
          <>
            <Line label={t('shop.reconciliation.invoiced')}><Money amount={totals.invoiced ?? '0.0000'} currency={pi.currency} /></Line>
            <Line label={t('shop.reconciliation.outstanding')}><Money amount={totals.outstanding ?? '0.0000'} currency={pi.currency} /></Line>
          </>
        ) : null}
      </div>
      {/* FN-06 in one sentence, in R-22's own words, beside the only action that leads to an
          invoice — so the link reads as "go and look at it", never as "pay it here". Shown only
          where there is an invoice to open. */}
      {data.pieces.some((p) => actionsFor(p, permissions).includes('VIEW_INVOICE')) ? (
        <div className="mk-pbody"><p className="mk-hint">{t('payables.howToPay')}</p></div>
      ) : null}
      {confirming ? (
        <ReportSale
          piece={confirming}
          currency={pi.currency}
          today={dashboard.data?.today ?? null}
          onClose={() => setConfirming(null)}
          onDone={async () => {
            setConfirming(null);
            // In place: the tally and its totals, and the consignment reads the sale moves. The
            // row changes where it stands and nothing reloads the page.
            await invalidateAll(qc, [
              ['shop', 'consignment-invoices', pi.id, 'reconciliation'],
              ['shop', 'inventory'], ['shop', 'sold'], ['shop', 'dashboard'],
            ]);
          }}
        />
      ) : null}
    </Panel>
  );
}

function OrdersPanel({ pi }: { pi: ShopConsignmentInvoice }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(pi.orders.map((o) => o.orderId)));
  const toggle = (id: string) => setOpen((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Panel title={t('shop.consignmentInvoice.ordersTitle')}>
      {pi.orders.map((o) => {
        const isOpen = open.has(o.orderId);
        return (
          <div key={o.orderId} data-testid="pi-order">
            <div className="mk-pi-order-head">
              <button
                type="button"
                className="mk-pi-twisty"
                aria-expanded={isOpen}
                aria-label={isOpen ? t('shop.consignmentInvoice.collapse') : t('shop.consignmentInvoice.expand')}
                onClick={() => toggle(o.orderId)}
              >
                {isOpen ? '▾' : '▸'}
              </button>
              <h3>
                <Link to={`/shop/orders/${o.orderId}`}>{o.reference}</Link>
              </h3>
              <span className="mk-spacer" />
              <span className="mk-dim">{t('shop.consignmentInvoice.orderTotal')}</span>
              <Money amount={o.orderAmount} currency={pi.currency} />
            </div>
            {isOpen ? (
              <Table
                rows={o.lines}
                rowKey={(l) => `${o.orderId}:${l.description ?? ''}:${l.unitPrice}:${l.quantity}`}
                columns={[
                  { header: t('shop.consignmentInvoice.colProduct'), render: (l) => l.description ?? dash },
                  { header: t('shop.consignmentInvoice.colQty'), numeric: true, render: (l) => l.quantity },
                  { header: t('shop.consignmentInvoice.colUnitPrice'), numeric: true, render: (l) => <Money amount={l.unitPrice} currency={pi.currency} /> },
                  { header: t('shop.consignmentInvoice.colAmount'), numeric: true, render: (l) => <Money amount={l.lineAmount} currency={pi.currency} /> },
                ]}
              />
            ) : null}
          </div>
        );
      })}
    </Panel>
  );
}

/**
 * What this despatch is worth and what it costs — the same seven figures Admin sees, from the
 * same `payableView` on the server (SH-06, SH-13).
 *
 * The goods value is here and stays here: the retailer has to be able to see what was sent. It is
 * simply not a debt, and the note says so beside the figure rather than leaving a reader to infer
 * it from an absence. Nothing is payable on a despatch (SH-06), and since 04-Nov-2026 there is no
 * courier charge either.
 */
function AmountsPanel({ pi }: { pi: ShopConsignmentInvoice }) {
  const { t } = useTranslation();
  const a = pi.amounts;
  // The server returns the remaining balance; a negative one is a credit and is shown as one, never as "-x remaining".
  const inCredit = a.remaining.startsWith('-');
  const nothingPayable = isZeroAmount(a.totalPayable);
  return (
    <Panel title={t('despatch.panelTitle')} pad>
      <Line label={t('despatch.consignedValue')}><Money amount={a.consignedValue} currency={pi.currency} /></Line>
      <Note>{t('despatch.titleNote')}</Note>
      <Line label={t('despatch.tax')}><span className="mk-dim">{t('despatch.taxNote')}</span></Line>
      <Line label={t('despatch.payable')} strong>
        {nothingPayable
          ? <span className="mk-dim">{t('despatch.nothingPayable')}</span>
          : <Money amount={a.totalPayable} currency={pi.currency} />}
      </Line>
      {nothingPayable ? null : (
        <>
          <Line label={t('despatch.paid')}><Money amount={a.payableReceived} currency={pi.currency} /></Line>
          <Line label={inCredit ? t('despatch.credit') : t('despatch.stillToPay')} strong>
            <Money amount={inCredit ? a.remaining.slice(1) : a.remaining} currency={pi.currency} />
          </Line>
        </>
      )}
      {/* Money that really arrived under the arrangement retired at v1.6. It settles nothing on a
          consignment, so it is reported apart from the payable instead of being dropped. */}
      {isZeroAmount(a.goodsPaymentsReceived) ? null : (
        <>
          <Line label={t('despatch.goodsPayments')}><Money amount={a.goodsPaymentsReceived} currency={pi.currency} /></Line>
          <Note>{t('despatch.goodsPaymentsNote')}</Note>
        </>
      )}
    </Panel>
  );
}

// ------------------------------------------------------------ the delivery tally, and its actions

/**
 * W4 — report a sale without leaving the delivery.
 *
 * The same route My Inventory calls (`POST /shop/inventory/sold`, `shop.report_sale`, `zMarkSold`),
 * and deliberately the same words: the confirmation says what becomes payable (SL-02) and that the
 * sale cannot be undone from here, because a retailer meeting this dialog on two screens must not
 * have to read it twice to believe it says the same thing. No selling price, no customer (D67).
 *
 * SL-04 — today, or up to seven days back — is the ROUTE's rule, and so is every other refusal:
 * a piece that is not held, one on an APPROVED return (SL-01, CN-14, which the tally is not told
 * about), a retailer whose status may not report a sale. None of it is re-decided here. The picker
 * offers only dates inside the contract's own `saleDateBounds`, and whatever the route refuses is
 * shown as it came back.
 *
 * One piece at a time: the tally is the record of a delivery, not a worklist. Reporting a day's
 * sales in one go belongs to R-20, which has the selection for it.
 */
function ReportSale({ piece, currency, today, onClose, onDone }: {
  piece: ReconciledPiece & { pieceId: string };
  currency: string;
  today: string | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [saleDate, setSaleDate] = useState(today ?? '');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [waiting, setWaiting] = useState<NotYet | null>(null);
  const bounds = today ? saleDateBounds(today) : null;

  async function confirm() {
    setBusy(true);
    setRefusal(null);
    try {
      // Today is the default and is sent as no date at all, so the server applies the PORTAL's
      // today (D49) rather than this browser's.
      const body: MarkSold = {
        pieceIds: [piece.pieceId],
        ...(saleDate && saleDate !== today ? { saleDate } : {}),
      };
      await api.post('/shop/inventory/sold', body);
      await onDone();
    } catch (err) {
      // An unbuilt service and a refusal must not look alike (`notYet` decides, and nothing else).
      const n = notYet(err);
      if (n) setWaiting(n);
      else setRefusal(err instanceof ApiError ? err.message : t('myInventory.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('myInventory.confirmTitle', { n: 1 })}
      onClose={onClose}
      footer={(
        <>
          <Button tone="ghost" onClick={onClose}>{t('myInventory.cancel')}</Button>
          <Button disabled={busy || !!waiting} onClick={() => void confirm()}>{t('myInventory.confirm')}</Button>
        </>
      )}
    >
      {/* Only an inventory audience is sent a description (DB-07). */}
      {piece.description !== undefined ? <Line label={t('myInventory.colPiece')}>{piece.description}</Line> : null}
      <p>
        {t('myInventory.becomesPayable')} <Money amount={piece.wholesaleValue} currency={currency} />
      </p>
      {bounds ? (
        <Field label={t('myInventory.saleDate')} hint={t('myInventory.saleDateHint')}>
          <input type="date" value={saleDate} min={bounds.min} max={bounds.max} onChange={(e) => setSaleDate(e.target.value)} />
        </Field>
      ) : null}
      <Note>{t('myInventory.cannotUndo')}</Note>
      {waiting ? (
        <Note>
          {t('myInventory.notYet')}
          {waiting.waitingFor ? <span className="mk-dim"> ({waiting.waitingFor})</span> : null}
        </Note>
      ) : null}
      {refusal ? <Alert tone="stop">{refusal}</Alert> : null}
    </Modal>
  );
}

/**
 * R-26 (and the panel on R-07) — one CONSIGNMENT INVOICE: a delivery Miraki raised for this
 * account's orders, what became of its pieces, the orders behind it, and its shipment.
 *
 * The consignment invoice is the DESPATCH document. The goods on it are consigned — title stays
 * with Miraki until a piece is reported sold (SH-06) — so NOTHING on it is payable except the
 * courier charge (SH-13), and `paymentStatus` has its own `NOT_PAYABLE` state, which is not a
 * shade of unpaid. The document the retailer actually pays is the PURCHASE invoice, reached from the
 * tally's "View invoice" link and shown on R-22 Payables.
 *
 * Read-only except for the two actions on the tally — reporting a sale (W4) and opening the SALE
 * invoice a sold piece was billed on (W5) — and the courier charge, which is an order payment.
 */
export function ShopConsignmentInvoiceDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const back = backFromInvoice(location.state);
  const backState = stateAfterBack(location.state);
  const { data: pi, isLoading, error } = useShopConsignmentInvoice(id);

  if (isLoading) {
    return (
      <div role="status">
        <Spinner />
        <span className="mk-dim">{t('shop.consignmentInvoice.loading')}</span>
      </div>
    );
  }
  if (error) return <Alert tone="stop">{t('shop.consignmentInvoice.loadFailed')}</Alert>;
  if (!pi) return null;

  return (
    <>
      {/* A detail screen reached from a list needs a way back to it. Browser back
          works, but a person who arrived from a link, a bookmark or a pasted URL
          has no history to go back through. */}
      <div className="mk-backlink">
        <Link to={back} state={backState}>&larr; {t(back === ORDERS_HOME ? 'shop.backToOrders' : back.startsWith(INVOICES_HOME) ? 'shop.backToInvoices' : 'shop.backToThisOrder')}</Link>
      </div>
      <PageHeader
        title={pi.reference}
        sub={t('shop.consignmentInvoice.createdOn', { date: new Date(pi.createdAt).toLocaleDateString() })}
        actions={(
          <>
            {pi.shipment
              ? <Chip tone={SHIPMENT_TONE[pi.shipment.status]}>{shipmentStateLabel(pi.shipment.status)}</Chip>
              : <Chip tone="us">{t(`shop.consignmentInvoice.status${pi.status}`)}</Chip>}
            <Chip tone={PAYMENT_TONE[pi.amounts.paymentStatus]}>{t(payableStatusKey(pi.amounts.paymentStatus))}</Chip>
          </>
        )}
      />
      <ReconciliationPanel pi={pi} />
      <OrdersPanel pi={pi} />
      <div className="mk-pi-split">
        <AmountsPanel pi={pi} />
        <ShipmentPanel pi={pi} />
      </div>
    </>
  );
}
